import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

export const CalendarStatusSchema = z.enum([
  'queued',
  'inventorying',
  'mapping_coverage',
  'identifying_pillars',
  'sequencing',
  'rendering',
  'completed',
  'failed',
  'cancelled',
]);
export type CalendarStatus = z.infer<typeof CalendarStatusSchema>;

export const CalendarItemTypeSchema = z.enum(['pillar', 'spoke', 'refresh']);
export type CalendarItemType = z.infer<typeof CalendarItemTypeSchema>;

export const CalendarItemStatusSchema = z.enum([
  'planned',
  'brief_generated',
  'published',
  'cancelled',
]);
export type CalendarItemStatus = z.infer<typeof CalendarItemStatusSchema>;

export const CreateCalendarRequestSchema = z.object({
  project_id: UuidSchema,
  period_months: z.number().int().min(1).max(12).default(3),
  capacity_per_month: z.number().int().min(1).max(60).default(8),
  keyword_run_id: UuidSchema.optional(),
  audit_id: UuidSchema.optional(),
});
export type CreateCalendarRequest = z.infer<typeof CreateCalendarRequestSchema>;

export const ContentCalendarSchema = z.object({
  id: UuidSchema,
  tenant_id: UuidSchema,
  project_id: UuidSchema,
  status: CalendarStatusSchema,
  period_months: z.number().int(),
  capacity_per_month: z.number().int(),
  keyword_run_id: UuidSchema.nullable(),
  audit_id: UuidSchema.nullable(),
  rationale: z.string().nullable(),
  started_at: IsoDateTimeSchema.nullable(),
  completed_at: IsoDateTimeSchema.nullable(),
  failed_at: IsoDateTimeSchema.nullable(),
  failure_reason: z.string().nullable(),
  created_at: IsoDateTimeSchema,
});
export type ContentCalendar = z.infer<typeof ContentCalendarSchema>;

export const ContentCalendarItemSchema = z.object({
  id: UuidSchema,
  calendar_id: UuidSchema,
  scheduled_for: z.string(),
  type: CalendarItemTypeSchema,
  title: z.string(),
  target_keyword: z.string().nullable(),
  cluster_id: UuidSchema.nullable(),
  pillar_item_id: UuidSchema.nullable(),
  rationale: z.string().nullable(),
  status: CalendarItemStatusSchema,
  sort_order: z.number().int(),
  /** Linked brief (A3) that fulfils this item. Null until generated / linked. */
  brief_id: UuidSchema.nullable(),
});
export type ContentCalendarItem = z.infer<typeof ContentCalendarItemSchema>;

/** Response of POST /v1/calendar-items/:id/brief. */
export const CalendarItemBriefResponseSchema = z.object({
  calendar_item_id: UuidSchema,
  brief_id: UuidSchema,
  /** `linked` = we matched an existing brief; `queued` = a new brief workflow was started. */
  linkage: z.enum(['linked', 'queued']),
});
export type CalendarItemBriefResponse = z.infer<typeof CalendarItemBriefResponseSchema>;

/** LLM output for sequencing the calendar. */
export const CalendarSequenceOutputSchema = z.object({
  rationale: z.string().min(20).max(2000),
  items: z
    .array(
      z.object({
        title: z.string().min(3).max(160),
        type: CalendarItemTypeSchema,
        target_keyword: z.string().nullable().optional(),
        cluster_label: z.string().nullable().optional(),
        rationale: z.string().min(5).max(280),
        month_offset: z.number().int().min(0).max(11),
        sort_in_month: z.number().int().min(0).max(60),
      }),
    )
    .min(1)
    .max(120),
});
export type CalendarSequenceOutput = z.infer<typeof CalendarSequenceOutputSchema>;
